import { useUserContext } from '@nrdy-marketing-engine/server/src/user/context';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CartState } from '../../@types/cart-state';
import createCart from '../api/cart/create-cart';
import fetchCart from '../api/cart/fetch-cart';
import updateCart from '../api/cart/update-cart';
import createLineItem from '../api/line-item/create-line-item';
import removeLineItem from '../api/line-item/remove-line-item';
import updateLineItem from '../api/line-item/update-line-item';
import { transformMeToClient } from '../api/user/create-account';
import useCatalogItemFetch from './use-catalog-item-fetch';

function useCartController() {
  const { user, students } = useUserContext();

  const client = useMemo(() => transformMeToClient(user), [user]);

  const {
    loading: cartLoading,
    catalogItem: originalCatalogItem,
    cartJurisdictionId,
    missingCatalogItem,
    fetchAttempted,
    error: catalogItemError,
  } = useCatalogItemFetch();
  const [cartState, setCartState] = useState<CartState>({});

  const handleCartResponse = useCallback((currentCart, cartResponse) => {
    return {
      ...currentCart,
      cartId: cartResponse?.data?.id || currentCart.cartId,
      cart: cartResponse?.data?.attributes || { ...currentCart.cart },
      lineItems: selectLineItems(cartResponse?.included),
    };
  }, []);

  const selectLineItems = (lineItemsData) =>
    lineItemsData
      ? lineItemsData
          .filter((item) => item.type === 'line_items')
          .map((item) => ({ id: item.id, ...item.attributes }))
      : [];

  const removePromoCode = () => {
    if (!cartState.cartId) return;

    return updateCart(cartState.cartId, { promo_codes: null }).then((res) => {
      setCartState((state) => ({ ...state, ...handleCartResponse(cartState, res) }));
    });
  };

  const applyPromoCode = (promoCode) => {
    if (!cartState.cartId || !cartState.catalogItem || !promoCode) return Promise.reject();

    return updateCart(cartState.cartId, { promo_codes: [{ code: promoCode }] }).then((res) => {
      if (!res?.data?.attributes?.promo_codes) return Promise.reject();

      setCartState((state) => ({ ...state, ...handleCartResponse(cartState, res) }));

      return Promise.resolve(res.data.attributes.promo_codes[0].code);
    });
  };

  const setupCart = useCallback(
    async (client, catalogItem, cartJurisdictionId, student) => {
      let cart = handleCartResponse({}, await createCart(client, cartJurisdictionId));

      if (cart.lineItems && cart.lineItems.length > 0) {
        await removeLineItem(cart.cartId, cart.lineItems[0].id);
        cart = handleCartResponse(cart, await fetchCart(cart.cartId));
      }

      if (cart.cart.status === '') {
        cart = handleCartResponse(cart, await fetchCart(cart.cartId));
      }

      if (cart.cart.status === 'empty') {
        await createLineItem(cart.cartId, catalogItem);

        cart = handleCartResponse(cart, await fetchCart(cart.cartId));
        await updateLineItem(
          cart.cartId,
          cart.lineItems[0].id,
          client.id,
          cart.lineItems[0],
          student
        );

        cart = handleCartResponse(cart, await fetchCart(cart.cartId));
      }

      return cart;
    },
    [handleCartResponse]
  );

  /*
    The purpose of fetchAttempted here is so that it blocks the creation of the cart until 
    the catalog item has been fetched. The reason for this is because if the item is coming from a quote
    we need to make sure that the cart is created with the jurisdiction id of the quote and not the default
    as this would impact the price of the item since the price is based on the jurisdiction id.
  */
  useEffect(() => {
    if (!fetchAttempted) return;

    if (!client || !originalCatalogItem || !cartJurisdictionId || !students) return;

    const student = students && students.length > 0 ? students[0] : null;

    setupCart(client, originalCatalogItem, cartJurisdictionId, student).then((cart) => {
      setCartState((state) => ({
        ...state,
        ...cart,
      }));
    });
  }, [client, originalCatalogItem, setupCart, fetchAttempted, cartJurisdictionId, students]);

  useEffect(
    function _trackClient() {
      client?.uuid !== cartState.client?.uuid && setCartState((state) => ({ ...state, client }));
    },
    [client, cartState]
  );

  useEffect(
    //price precedence was set based on the following.
    /*
    1. Quoted price cents is first since we want to use the price that was picked by sales to be supplied with the quote (if it exists).
    This would include a discounted price coming from them. Since we are not currently applying promo codes to quoted items just yet, this should take priority. 
    2. Otherwise we use the line item price, which is calculated based on the price calculated from the item sent to the cart API which would include a discounted price from the UI
    3. Otherwisee default to the catalogitem default price in cents
    */
    function _syncCartAndLineItemPrices() {
      let cart = { ...cartState };
      const lineItem = cartState.lineItems && cartState.lineItems[0];
      let cartPrice =
        cartState.catalogItem?.quoted_price_cents ||
        lineItem?.final_price_cents ||
        cartState.catalogItem?.price_cents;
      cart.cartPrice = cartPrice;
      setCartState(cart);
    },
    [cartState.lineItems, cartState.catalogItem]
  );

  useEffect(
    function _trackCatalogItem() {
      cartJurisdictionId !== cartState.cartJurisdictionId &&
        setCartState((state) => ({ ...state, cartJurisdictionId }));

      originalCatalogItem &&
        cartState.catalogItem?.id !== originalCatalogItem?.id &&
        setCartState((state) => ({ ...state, catalogItem: originalCatalogItem }));
    },
    [originalCatalogItem, cartJurisdictionId, cartState]
  );

  return {
    applyPromoCode,
    cartLoading,
    cartState,
    handleCartResponse,
    missingCatalogItem,
    catalogItemError,
    originalCatalogItem,
    removePromoCode,
    selectLineItems,
    setupCart,
  };
}

export default useCartController;
